import {useCallback,useEffect,useRef,useState} from 'react';
import {useDispatch} from 'react-redux';
import {axiosInstance} from '../config';
import {getAdminMedicineCatalogue,invalidateStorefrontMedicines} from '../reduxToolkit/features/adminPanel/medicine';
import {getNotification} from '../reduxToolkit/features/adminPanel/notification';
import {requestErrorDetails} from './requestError.mjs';

export default function useMedicineActivity({sourceId,page,pageSize,status,hidden,polling=true}) {
  const [data,setData]=useState(null),[error,setError]=useState(''),[attempt,setAttempt]=useState(0),[loading,setLoading]=useState(true),[refreshing,setRefreshing]=useState(false);
  const previous=useRef(new Map()),dispatch=useDispatch(),snapshot=useRef(''),currentData=useRef(null),source=useRef(sourceId),manual=useRef(false);
  const query=new URLSearchParams({page:String(page),pageSize:String(pageSize),status,hidden:hidden.join(',')});
  if(sourceId)query.set('sourceId',sourceId);
  const search=query.toString();
  useEffect(()=>{
    if(source.current!==sourceId){source.current=sourceId;previous.current.clear();snapshot.current='';currentData.current=null;setData(null);setError('');}
    if(!polling){setLoading(false);setRefreshing(false);return undefined;}
    let live=true,busy=false,timer;const controller=new AbortController();
    const manualRefresh=manual.current;manual.current=false;
    async function load(explicit=false){
      if(!live||busy)return;
      clearTimeout(timer);
      if(document.visibilityState==='hidden'){timer=setTimeout(load,15000);return;}
      busy=true;
      // Background polling is deliberately silent. Inserting a progress bar on
      // each poll moved every card and made the panel appear to blink.
      if(!currentData.current)setLoading(true);
      if(explicit)setRefreshing(true);
      let delay=15000;
      try{
        const {data:next}=await axiosInstance.get('/api/translations/activity?'+search,{signal:controller.signal,timeout:15000});
        if(!live)return;
        let finished=false,published=false;
        for(const row of next.rows){const key=row.kind+':'+row.id,old=previous.current.get(key);
          if(old&&old!==row.status&&['completed','completed_with_errors','failed','conflict','uncertain'].includes(row.status)){
            finished=true;if(['completed','completed_with_errors'].includes(row.status))published=true;
          }previous.current.set(key,row.status);
        }
        if(finished)dispatch(getNotification());if(published){dispatch(invalidateStorefrontMedicines());dispatch(getAdminMedicineCatalogue({force:true}));}
        const serialized=JSON.stringify(next);
        if(serialized!==snapshot.current){snapshot.current=serialized;currentData.current=next;setData(next);}
        setError('');delay=next.hasActive?4000:15000;
      }catch(e){if(live&&!controller.signal.aborted)setError(requestErrorDetails(e,{readOnly:true,fallback:'Activity could not be refreshed. Your requests remain saved.'}).message);}
      finally{busy=false;if(live){setLoading(false);setRefreshing(false);timer=setTimeout(load,delay);}}
    }
    load(manualRefresh);const wake=()=>{if(document.visibilityState!=='hidden')load();};
    window.addEventListener('focus',wake);document.addEventListener('visibilitychange',wake);
    return()=>{live=false;controller.abort();clearTimeout(timer);window.removeEventListener('focus',wake);document.removeEventListener('visibilitychange',wake);};
  },[dispatch,search,attempt,polling,sourceId]);
  return{data,error,loading,refreshing,refresh:useCallback(()=>{manual.current=true;setAttempt(value=>value+1);},[])};
}
